import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { MailCheck } from 'lucide-react'
import { onboardingContactQueryOptions } from '@/lib/queryOptions'
import { requestEmailVerification, useSession } from '@/lib/auth-client'
import { GlassButton } from '@/components/ui/GlassButton'
import { useT } from '@/i18n/provider'

export function EmailVerificationBanner() {
  const t = useT()
  const { data: session } = useSession()
  const [statusMessage, setStatusMessage] = useState('')
  const [error, setError] = useState('')

  const user = session?.user as { emailVerified?: boolean } | undefined
  const isVerified = user?.emailVerified === true

  const { data: contact } = useQuery({
    ...onboardingContactQueryOptions(),
    enabled: !!session?.user,
  })

  const pendingEmail = contact?.pendingEmail ?? null

  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!pendingEmail) throw new Error(t('auth.verifyBanner.sendFailed'))
      const callbackURL = typeof window !== 'undefined'
        ? `${window.location.origin}/dashboard`
        : '/dashboard'
      const res = await requestEmailVerification(pendingEmail, callbackURL)
      if (res?.error) {
        throw new Error(res.error.message ?? t('auth.verifyBanner.sendFailed'))
      }
    },
    onSuccess: () => {
      setStatusMessage(t('auth.verifyBanner.sent'))
      setError('')
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : t('auth.verifyBanner.sendFailed'))
      setStatusMessage('')
    },
  })

  if (!session?.user || isVerified || !pendingEmail) return null

  return (
    <div className="mx-6 mt-4 rounded-2xl border border-amber-400/25 bg-amber-500/[0.08] p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-xl bg-amber-500/20 p-2">
            <MailCheck size={16} className="text-amber-300" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-amber-100">{t('auth.verifyBanner.title')}</p>
            <p className="text-sm text-amber-50/85">
              {t('auth.verifyBanner.subtitle', { email: pendingEmail })}
            </p>
            {statusMessage && <p className="text-xs text-emerald-300">{statusMessage}</p>}
            {error && <p className="text-xs text-red-300">{error}</p>}
          </div>
        </div>
        <GlassButton
          size="sm"
          onClick={() => sendMutation.mutate()}
          loading={sendMutation.isPending}
        >
          {t('auth.verifyBanner.sendAction')}
        </GlassButton>
      </div>
    </div>
  )
}
